/**
 * Quick Order – Storefront API search, row management, Add All to Cart
 * ES module; custom element quick-order-section.
 */
// @ts-nocheck
import { ThemeEvents } from '@theme/events';

const SEARCH_DEBOUNCE_MS = 400;
const SEARCH_MIN_LENGTH = 2;
const SEARCH_FIRST = 12;

const searchQuery = `
  query QuickOrderSearch($query: String!, $first: Int) {
    search(query: $query, first: $first, types: [PRODUCT], unavailableProducts: SHOW) {
      nodes {
        ... on Product {
          id
          title
          handle
          featuredImage {
            url(transform: { maxWidth: 120, maxHeight: 120 })
          }
          options(first: 5) {
            name
            values
          }
          variants(first: 25) {
            nodes {
              id
              sku
              availableForSale
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }
  }
`;

function parseVariantId(gid) {
  if (!gid) return '';
  const parts = gid.split('/');
  return parts[parts.length - 1] || gid;
}

function debounce(fn, ms) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), ms);
  };
}

function escapeHtml(s) {
  if (s == null) return '';
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

class QuickOrderSection extends HTMLElement {
  constructor() {
    super();
    this.apiUrl = '';
    this.token = '';
    this.searchAbort = null;
    this.searchRequestId = 0;
    this.rowIndex = 0;
    this.mainSearchItems = [];
    this.mainSearchFocusedIndex = -1;
    this.lastSelectedFromMainSearch = null;
    this._boundDocumentClick = null;
    this._saveStateDebounced = null;
  }

  connectedCallback() {
    this.apiUrl = (this.getAttribute('data-storefront-api-url') || '').trim();
    this.token = (this.getAttribute('data-storefront-token') || '').trim();
    this.tbody = this.querySelector('[data-quick-order-tbody]');
    this.mobileCards = this.querySelector('[data-quick-order-mobile-cards]');
    this.totalEl = this.querySelector('[data-quick-order-total]');
    this.addAllBtn = this.querySelector('[data-quick-order-add-all]');
    this.clearBtn = this.querySelector('[data-quick-order-clear]');
    this.messageEl = this.querySelector('[data-quick-order-message]');

    if (this.addAllBtn) this.addAllBtn.addEventListener('click', () => this._onAddAllClick());
    if (this.clearBtn) this.clearBtn.addEventListener('click', () => this.clearAll());

    if (!this.apiUrl || !this.tbody) return;

    this._saveStateDebounced = debounce(() => this.saveState(), 200);
    this._boundDocumentClick = (e) => this._onDocumentClick(e);
    document.addEventListener('click', this._boundDocumentClick);

    this.addEventListener(ThemeEvents.quantitySelectorUpdate, (e) => this._onQuantitySelectorUpdate(e));

    this.setupMainSearch();
    try {
      const key = this._getStorageKey();
      const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.restoreState(parsed);
          return;
        }
      }
    } catch (_) {}
    this._setupExistingRows();
    this.updateTotal();
    this.renderMobileCards();
  }

  disconnectedCallback() {
    if (this.searchAbort) {
      this.searchAbort.abort();
      this.searchAbort = null;
    }
    if (this._boundDocumentClick) {
      document.removeEventListener('click', this._boundDocumentClick);
      this._boundDocumentClick = null;
    }
    this.tbody = null;
    this.mobileCards = null;
    this.totalEl = null;
    this.addAllBtn = null;
    this.clearBtn = null;
    this.messageEl = null;
  }

  _setupExistingRows() {
    this.tbody?.querySelectorAll('[data-quick-order-row]').forEach((tr) => {
      const rowSearch = tr.querySelector('[data-quick-order-row-search]');
      const rowResults = tr.querySelector('[data-quick-order-row-results]');
      if (rowSearch && rowResults) {
        const debouncedRun = debounce(() => this.runSearch(rowSearch, rowResults, tr), SEARCH_DEBOUNCE_MS);
        rowSearch.addEventListener('input', debouncedRun);
        rowSearch.addEventListener('focus', () => {
          if ((rowSearch.value || '').trim().length >= SEARCH_MIN_LENGTH) debouncedRun();
        });
        rowSearch.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            rowResults.setAttribute('hidden', '');
            rowResults.innerHTML = '';
            rowSearch.blur();
          }
        });
      }
      const removeBtn = tr.querySelector('[data-quick-order-remove]');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => {
          tr.remove();
          this.updateTotal();
          this.renderMobileCards();
          this.saveState();
        });
      }
      const qtyInput = tr.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
      if (qtyInput) qtyInput.addEventListener('input', () => this.updateTotal());
    });
  }

  _onDocumentClick(e) {
    const target = e.target;
    const section = this;
    const insideMainSearch = section.querySelector('.quick-order__search-wrap')?.contains(target);
    const insideAnyRowSearch = Array.from(section.querySelectorAll('.quick-order__row-search-wrap')).some((wrap) => wrap.contains(target));
    const insideAnyResults = Array.from(section.querySelectorAll('.quick-order__results')).some((el) => el.contains(target));
    if (insideMainSearch || insideAnyRowSearch || insideAnyResults) return;
    section.querySelectorAll('.quick-order__results').forEach((el) => {
      el.innerHTML = '';
      el.setAttribute('hidden', '');
    });
    const mainInput = section.querySelector('#quick-order-search-main');
    if (mainInput) mainInput.setAttribute('aria-expanded', 'false');
    this.mainSearchItems = [];
    this.mainSearchFocusedIndex = -1;
  }

  _onQuantitySelectorUpdate(e) {
    const fromRow = e.target.closest?.('tr[data-quick-order-row]');
    const fromCard = e.target.closest?.('.quick-order__mobile-card');
    if (fromRow) {
      this.updateTrashMinusVisibility(fromRow);
      this.updateTotal();
      this.renderMobileCards();
    } else if (fromCard && e.detail?.quantity != null) {
      const idx = parseInt(fromCard.getAttribute('data-row-index'), 10);
      const rows = this.tbody?.querySelectorAll('tr[data-quick-order-row]') || [];
      const row = rows[idx];
      if (row) {
        const rowInput = row.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
        if (rowInput) rowInput.value = String(e.detail.quantity);
      }
      this.updateTotal();
      this.updateMobileCardInPlace(fromCard, row);
    }
  }

  updateMobileCardInPlace(cardEl, rowEl) {
    if (!cardEl || !rowEl) return;
    const subtotalEl = rowEl.querySelector('[data-quick-order-subtotal]');
    const cardSubtotalEl = cardEl.querySelector('[data-quick-order-subtotal]');
    if (subtotalEl && cardSubtotalEl) cardSubtotalEl.textContent = subtotalEl.textContent;
    const qtyInput = rowEl.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
    const minusBtn = cardEl.querySelector('quantity-selector-component button[name="minus"]');
    const trashBtn = cardEl.querySelector('.quick-order__qty-trash');
    if (qtyInput && minusBtn && trashBtn) {
      const v = qtyInput.value;
      const isOne = v === '1' || parseInt(v, 10) === 1;
      trashBtn.style.display = isOne ? '' : 'none';
      minusBtn.style.display = isOne ? 'none' : '';
    }
  }

  _onAddAllClick() {
    if (!this.addAllBtn) return;

    const rows = this.getRowsData().filter((r) => r.available && r.quantity >= 1);
    if (!rows.length) {
      this.showMessage(this.t('add_all_no_items'), 'error');
      return;
    }
    if (!this.token) {
      this.showMessage(this.t('no_token'), 'error');
      return;
    }
    this.addAllBtn.disabled = true;
    this.hideMessage();
    const root = (window.Shopify?.routes?.root) || '/';
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });
    let added = 0;
    let err = null;
    let lastSections = null;
    const payload = { items: rows.map((r) => ({ id: parseInt(r.variantId, 10), quantity: r.quantity })).filter((r) => r.id) };
    if (sectionIds.length) payload.sections = sectionIds.join(',');
    payload.sections_url = window.location.pathname;
    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 422 || (data.description || data.message)) throw new Error(data.description || data.message);
        if (data.items?.length) data.items.forEach((item) => { added += item.quantity; });
        lastSections = data.sections;
      })
      .catch((e) => { err = e; })
      .finally(() => {
        if (this.addAllBtn) this.addAllBtn.disabled = false;
        this.updateTotal();
        if (err) this.showMessage(`${this.t('add_all_error')} ${err.message || ''}`, 'error');
        else {
          this.showMessage(this.t('items_added').replace('{{ count }}', String(added)), 'success');
          if (lastSections) {
            try {
              document.dispatchEvent(new CustomEvent('cart:update', {
                bubbles: true,
                detail: { resource: {}, sourceId: 'quick-order', data: { sections: lastSections } }
              }));
            } catch (_) {}
          }
          const cartDrawer = document.querySelector('cart-drawer-component');
          if (cartDrawer && typeof cartDrawer.open === 'function') requestAnimationFrame(() => cartDrawer.open());
          this.clearAll();
        }
      });
  }

  t(key) {
    const k = `quick_order.${key}`;
    try {
      if (window.Theme && typeof Theme.t === 'function') return Theme.t(k);
      if (window.Shopify?.locale?.translations?.[k]) return window.Shopify.locale.translations[k];
    } catch (_) {}
    const defaults = {
      search_placeholder: 'Enter SKU or product name',
      add_row: 'Add Row',
      sold_out: 'Sold out',
      continue_shopping: 'Continue Shopping',
      clear_all: 'CLEAR',
      view_cart: 'View Cart',
      add_all_to_cart: 'Add All to Cart',
      subtotal: 'Subtotal',
      total: 'Total',
      total_value: '$0.00',
      remove: 'Remove',
      options: 'Options',
      qty: 'Qty',
      decrease_qty: 'Decrease quantity',
      increase_qty: 'Increase quantity',
      product: 'Product',
      sku: 'SKU',
      price: 'Price',
      search_no_results: 'No products found',
      search_searching: 'Searching…',
      items_added: '{{ count }} items added to cart',
      add_all_error: 'Error adding items to cart',
      add_all_no_items: 'Add at least one product with quantity.',
      no_token: 'Storefront API token is not configured.'
    };
    return defaults[key] ?? key;
  }

  formatMoney(amount, currencyCode) {
    if (amount == null || amount === '') return '$0.00';
    const amountNum = typeof amount === 'string' ? parseFloat(amount, 10) : Number(amount);
    if (isNaN(amountNum)) return '$0.00';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const cents = Math.round(amountNum * 100);
      return window.Shopify.formatMoney(cents, '${{amount}}');
    }
    return `$${amountNum.toFixed(2)}`;
  }

  getPriceHtml(item) {
    const priceVal = item.priceAmount != null ? item.priceAmount : item.price;
    const priceFormatted = this.formatMoney(priceVal, item.currencyCode);
    const onSale = item.compareAtPrice != null && item.compareAtPrice > item.price;
    if (onSale) {
      const compareFormatted = this.formatMoney(item.compareAtPriceAmount != null ? item.compareAtPriceAmount : item.compareAtPrice, item.currencyCode);
      return `<div class="quick-order__price-wrap is_compare_price">` +
        `<div class="sale_badge">Sale</div>` +
        `<span class="price price--on-sale">${escapeHtml(priceFormatted)}</span> ` +
        `<s class="compare-at-price">${escapeHtml(compareFormatted)}</s>` +
        `</div>`;
    }
    return `<div class="quick-order__price-wrap">` +
      `<span class="price">${escapeHtml(priceFormatted)}</span>` +
      `</div>`;
  }

  showMessage(text, type) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text;
    this.messageEl.hidden = false;
    this.messageEl.className = `quick-order__message quick-order__message--${type || 'info'}`;
    if (type === 'success' || type === 'error') setTimeout(() => { this.messageEl.hidden = true; }, 4000);
  }

  hideMessage() {
    if (this.messageEl) this.messageEl.hidden = true;
  }

  getRowsData() {
    const rows = [];
    this.tbody?.querySelectorAll('[data-quick-order-row]').forEach((tr) => {
      const variantId = tr.getAttribute('data-variant-id');
      if (!variantId) return;
      const qtyInput = tr.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
      const qty = Math.max(0, parseInt(qtyInput?.value ?? '0', 10) || 0);
      rows.push({ variantId, quantity: qty, available: tr.getAttribute('data-available') === 'true' });
    });
    return rows;
  }

  _getStorageKey() {
    const sectionId = this.getAttribute('data-section-id') || '';
    return `quick_order_${sectionId}`;
  }

  getState() {
    const state = [];
    this.tbody?.querySelectorAll('[data-quick-order-row]').forEach((tr) => {
      const variantIdNumeric = tr.getAttribute('data-variant-id');
      if (!variantIdNumeric) return;
      const productEl = tr.querySelector('[data-quick-order-product]');
      const productTitle = productEl ? (productEl.querySelector('a') ? productEl.querySelector('a').textContent : productEl.textContent) : '';
      const skuEl = tr.querySelector('.quick-order__cell-sku');
      const sku = skuEl ? skuEl.textContent : '';
      const qtyInput = tr.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
      const quantity = Math.max(1, parseInt(qtyInput?.value ?? '1', 10) || 1);
      state.push({
        variantIdNumeric,
        quantity,
        productHandle: tr.getAttribute('data-product-handle') || '',
        sku: sku.trim() || '—',
        productTitle: (productTitle || '').trim() || '—',
        imageUrl: tr.getAttribute('data-image-url') || '',
        priceAmount: tr.getAttribute('data-price') || '0',
        price: parseFloat(tr.getAttribute('data-price') || '0', 10),
        currencyCode: tr.getAttribute('data-currency-code') || 'USD',
        availableForSale: tr.getAttribute('data-available') === 'true',
        compareAtPriceAmount: tr.getAttribute('data-compare-price') || null
      });
    });
    return state;
  }

  saveState() {
    try {
      const key = this._getStorageKey();
      const state = this.getState();
      if (state.length) sessionStorage.setItem(key, JSON.stringify(state));
      else sessionStorage.removeItem(key);
    } catch (_) {}
  }

  restoreState(state) {
    if (!Array.isArray(state) || !state.length || !this.tbody) return;
    this.tbody.innerHTML = '';
    this.rowIndex = 0;
    state.forEach((item) => {
      this.addRow(null);
      const rows = this.tbody.querySelectorAll('[data-quick-order-row]');
      const tr = rows[rows.length - 1];
      if (tr) {
        this.selectResult(tr, item, true);
        const qtyInput = tr.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
        if (qtyInput && item.quantity != null) qtyInput.value = String(Math.max(1, item.quantity));
      }
    });
    this.updateTotal();
    this.renderMobileCards();
  }

  updateTotal() {
    let total = 0;
    let currencyCode = 'USD';
    this.tbody?.querySelectorAll('[data-quick-order-row]').forEach((tr) => {
      const variantId = tr.getAttribute('data-variant-id');
      const price = parseFloat(tr.getAttribute('data-price') || '0', 10);
      const qtyInput = tr.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
      const qty = Math.max(0, parseInt(qtyInput?.value ?? '0', 10) || 0);
      const available = tr.getAttribute('data-available') === 'true';
      const subtotalEl = tr.querySelector('[data-quick-order-subtotal]');
      if (variantId && price > 0 && subtotalEl) {
        const sub = price * qty;
        if (tr.getAttribute('data-currency-code')) currencyCode = tr.getAttribute('data-currency-code');
        subtotalEl.textContent = this.formatMoney(sub, currencyCode);
        if (available && qty >= 1) total += sub;
      }
    });
    if (this.totalEl) this.totalEl.textContent = this.formatMoney(total, currencyCode);
    const rows = this.getRowsData();
    const canAdd = rows.some((r) => r.available && r.quantity >= 1);
    if (this.addAllBtn) this.addAllBtn.disabled = !canAdd;
    this.updateActionsVisibility();
    if (this._saveStateDebounced) this._saveStateDebounced();
  }

  updateActionsVisibility() {
    const count = this.getRowsData().filter((r) => r.quantity >= 1).length;
    if (this.clearBtn) {
      this.clearBtn.hidden = count < 2;
    }
    if (this.addAllBtn) {
      this.addAllBtn.hidden = count < 1;
    }
  }

  flattenProducts(nodes) {
    const items = [];
    (nodes || []).forEach((node) => {
      if (!node?.variants?.nodes) return;
      const productTitle = node.title || '';
      const productHandle = node.handle || '';
      const imageUrl = node.featuredImage?.url || '';
      node.variants.nodes.forEach((v) => {
        const optionLabel = (v.selectedOptions || []).map((o) => o.value).filter(Boolean).join(' / ') || '—';
        items.push({
          variantId: v.id,
          variantIdNumeric: parseVariantId(v.id),
          sku: v.sku || '—',
          productTitle,
          productHandle,
          imageUrl,
          optionLabel,
          price: parseFloat(v.price?.amount ?? '0', 10),
          priceAmount: v.price?.amount != null ? String(v.price.amount) : null,
          compareAtPrice: v.compareAtPrice?.amount != null ? parseFloat(v.compareAtPrice.amount, 10) : null,
          compareAtPriceAmount: v.compareAtPrice?.amount != null ? String(v.compareAtPrice.amount) : null,
          currencyCode: v.price?.currencyCode || 'USD',
          availableForSale: !!v.availableForSale
        });
      });
    });
    return items;
  }

  renderResults(container, items, onSelect, isMainSearch) {
    if (!container) return;
    container.innerHTML = '';
    container.removeAttribute('hidden');
    if (!items.length) {
      const empty = document.createElement('div');
      empty.className = 'quick-order__result-item';
      empty.textContent = this.t('search_no_results');
      container.appendChild(empty);
      return;
    }
    items.forEach((item, idx) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `quick-order__result-item${item.availableForSale ? '' : ' quick-order__result-item--unavailable'}`;
      btn.setAttribute('role', 'option');
      if (isMainSearch) btn.setAttribute('data-index', String(idx));
      const thumbHtml = item.imageUrl
        ? `<span class="quick-order__result-thumb"><img src="${escapeHtml(item.imageUrl)}" alt="" width="40" height="40" loading="lazy"></span>`
        : '<span class="quick-order__result-thumb quick-order__result-thumb--placeholder"></span>';
      btn.innerHTML = thumbHtml +
        '<span class="quick-order__result-content">' +
        `<span class="quick-order__result-sku">${escapeHtml(item.sku)}</span>` +
        `<span class="quick-order__result-title">${escapeHtml(item.productTitle)}</span>` +
        '<div class="quick-order__result-meta">' +
        `<span class="quick-order__result-price">${this.getPriceHtml(item)}</span>` +
        (!item.availableForSale ? `<span class="quick-order-badge--soldout">${escapeHtml(this.t('sold_out'))}</span>` : '') +
        '</div></span>';
      btn.addEventListener('click', () => {
        onSelect(item);
        container.setAttribute('hidden', '');
        container.innerHTML = '';
      });
      if (isMainSearch) {
        btn.addEventListener('focus', () => {
          this.mainSearchFocusedIndex = parseInt(btn.getAttribute('data-index'), 10) || 0;
          container.querySelectorAll('.quick-order__result-item[data-index]').forEach((b) => b.classList.remove('quick-order__result-item--focused'));
          btn.classList.add('quick-order__result-item--focused');
        });
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = Math.min(parseInt(btn.getAttribute('data-index'), 10) + 1, items.length - 1);
            container.querySelector(`.quick-order__result-item[data-index="${next}"]`)?.focus();
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const idx = parseInt(btn.getAttribute('data-index'), 10) || 0;
            if (idx <= 0) {
              this.mainSearchFocusedIndex = -1;
              this.querySelector('#quick-order-search-main')?.focus();
            } else {
              const prev = idx - 1;
              const prevBtn = container.querySelector(`.quick-order__result-item[data-index="${prev}"]`);
              if (prevBtn) prevBtn.focus();
            }
          } else if (e.key === 'Enter') {
            e.preventDefault();
            onSelect(item);
            container.setAttribute('hidden', '');
            container.innerHTML = '';
          }
        });
      }
      container.appendChild(btn);
    });
    if (isMainSearch && items.length) {
      this.mainSearchItems = items;
      this.mainSearchFocusedIndex = 0;
      const firstBtn = container.querySelector('.quick-order__result-item[data-index="0"]');
      if (firstBtn) firstBtn.classList.add('quick-order__result-item--focused');
      /* Do not focus first result: keep focus in input so user can continue typing or clear */
    }
  }

  selectResult(rowEl, item) {
    if (!rowEl) return;
    const skipMobileRender = rowEl.hasAttribute('data-quick-order-skip-mobile-render');
    if (skipMobileRender) rowEl.removeAttribute('data-quick-order-skip-mobile-render');
    rowEl.classList.remove('quick-order__row--empty');
    rowEl.setAttribute('data-variant-id', item.variantIdNumeric);
    rowEl.setAttribute('data-product-handle', item.productHandle || '');
    rowEl.setAttribute('data-image-url', item.imageUrl || '');
    rowEl.setAttribute('data-price', item.priceAmount != null ? item.priceAmount : String(item.price));
    rowEl.setAttribute('data-available', item.availableForSale ? 'true' : 'false');
    rowEl.setAttribute('data-currency-code', item.currencyCode || 'USD');
    if (item.compareAtPriceAmount != null) rowEl.setAttribute('data-compare-price', item.compareAtPriceAmount);
    else if (item.compareAtPrice != null) rowEl.setAttribute('data-compare-price', String(item.compareAtPrice));
    else rowEl.removeAttribute('data-compare-price');
    const productEl = rowEl.querySelector('[data-quick-order-product]');
    if (productEl) {
      const handle = item.productHandle || '';
      const url = handle ? `/products/${handle}` : '#';
      productEl.innerHTML = handle ? `<a target="_blank" href="${escapeHtml(url)}" class="quick-order__product-link">${escapeHtml(item.productTitle)}</a>` : escapeHtml(item.productTitle);
    }
    const imageCell = rowEl.querySelector('.quick-order__cell--image');
    if (imageCell) {
      const handle = item.productHandle || '';
      const url = handle ? `/products/${handle}` : '#';
      if (item.imageUrl) {
        imageCell.innerHTML = `<a href="${escapeHtml(url)}" class="quick-order__cell-image-link"><img src="${escapeHtml(item.imageUrl)}" alt="" width="60" height="60" loading="lazy" class="quick-order__cell-image"></a>`;
      } else {
        imageCell.innerHTML = '<span class="quick-order__cell-image quick-order__cell-image--placeholder"></span>';
      }
    }
    const skuCell = rowEl.querySelector('.quick-order__cell--search');
    if (skuCell) {
      const wrap = skuCell.querySelector('.quick-order__row-search-wrap');
      if (wrap) {
        const handle = item.productHandle || '';
        const url = handle ? `/products/${handle}` : '#';
        const skuSpan = document.createElement(handle ? 'a' : 'span');
        skuSpan.target = '_blank';
        skuSpan.className = 'quick-order__cell-sku';
        if (handle) skuSpan.href = url;
        skuSpan.textContent = item.sku;
        wrap.innerHTML = '';
        wrap.appendChild(skuSpan);
      }
    }
    const qtyInput = rowEl.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
    if (qtyInput) {
      qtyInput.value = '1';
      qtyInput.closest('.quick-order__cell')?.style?.setProperty('display', '');
    }
    const subtotalEl = rowEl.querySelector('[data-quick-order-subtotal]');
    if (subtotalEl) subtotalEl.textContent = this.formatMoney(item.priceAmount ?? item.price, item.currencyCode);
    const removeBtn = rowEl.querySelector('[data-quick-order-remove]');
    if (removeBtn) removeBtn.style.visibility = 'visible';
    this.updateTrashMinusVisibility(rowEl);
    this.updateTotal();
    if (!skipMobileRender) this.renderMobileCards();
    this.saveState();
  }

  findRowByVariantId(variantIdNumeric) {
    if (!variantIdNumeric) return null;
    const rows = this.tbody?.querySelectorAll('[data-quick-order-row]') || [];
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute('data-variant-id') === String(variantIdNumeric)) return rows[i];
    }
    return null;
  }

  applyMainSearchSelection(item) {
    if (!item) return;
    this.lastSelectedFromMainSearch = item;
    const existingRow = this.findRowByVariantId(item.variantIdNumeric);
    if (existingRow) {
      const qtyInput = existingRow.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
      if (qtyInput) {
        const cur = Math.max(0, parseInt(qtyInput.value, 10) || 0);
        const max = Math.max(1, parseInt(qtyInput.getAttribute('max'), 10) || 9999);
        qtyInput.value = String(Math.min(cur + 1, max));
        this.updateTotal();
        this.renderMobileCards();
      }
    } else {
      this.addRow(item);
    }
    this.closeMainSearchOverlay();
  }

    setSearchLoading(container, loading) {
    if (!container) return;
    if (loading) {
      container.innerHTML = `<div class="quick-order__result-item quick-order__result-item--loading">${escapeHtml(this.t('search_searching'))}</div>`;
      container.removeAttribute('hidden');
    }
  }

  clearSearchResults(container, isMainSearch) {
    if (!container) return;
    container.innerHTML = '';
    container.setAttribute('hidden', '');
    if (isMainSearch) {
      this.mainSearchItems = [];
      this.mainSearchFocusedIndex = -1;
    }
  }

  runSearch(inputEl, resultsContainer, rowEl, isMainSearch) {
    const q = (inputEl?.value ?? '').trim();
    if (q.length < SEARCH_MIN_LENGTH) {
      if (this.searchAbort) {
        this.searchAbort.abort();
        this.searchAbort = null;
      }
      this.clearSearchResults(resultsContainer, isMainSearch);
      if (isMainSearch) this.querySelector('#quick-order-search-main')?.setAttribute('aria-expanded', 'false');
      return;
    }
    const myId = ++this.searchRequestId;
    if (isMainSearch) this.querySelector('#quick-order-search-main')?.setAttribute('aria-expanded', 'true');
    this.setSearchLoading(resultsContainer, true);
    this.searchStorefront(q).then((items) => {
      if (myId !== this.searchRequestId) return;
      this.setSearchLoading(resultsContainer, false);
      const onSelect = (item) => {
        if (isMainSearch) this.applyMainSearchSelection(item);
        else {
          const existingRow = this.findRowByVariantId(item.variantIdNumeric);
          if (existingRow && existingRow !== rowEl) {
            const qtyInput = existingRow.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
            if (qtyInput) {
              const cur = Math.max(0, parseInt(qtyInput.value, 10) || 0);
              const max = Math.max(1, parseInt(qtyInput.getAttribute('max'), 10) || 9999);
              qtyInput.value = String(Math.min(cur + 1, max));
              this.updateTotal();
              this.renderMobileCards();
            }
            return;
          }
          this.selectResult(rowEl, item);
        }
      };
      this.renderResults(resultsContainer, items, onSelect, isMainSearch);
      if (isMainSearch && resultsContainer) {
        this.querySelector('#quick-order-search-main')?.setAttribute('aria-expanded', items.length ? 'true' : 'false');
      }
    }).catch(() => {
      if (myId !== this.searchRequestId) return;
      this.setSearchLoading(resultsContainer, false);
      this.clearSearchResults(resultsContainer, isMainSearch);
    });
  }

  closeMainSearchOverlay() {
    const mainInput = this.querySelector('#quick-order-search-main');
    const mainResults = this.querySelector('#quick-order-results-main');
    if (mainInput) mainInput.value = '';
    if (mainResults) {
      mainResults.setAttribute('hidden', '');
      mainResults.innerHTML = '';
    }
    this.mainSearchItems = [];
    this.mainSearchFocusedIndex = -1;
    mainInput?.focus();
  }

  async searchStorefront(query) {
    if (!query || query.length < SEARCH_MIN_LENGTH) return [];
    if (!this.token) {
      this.showMessage(this.t('no_token'), 'error');
      return [];
    }
    if (this.searchAbort) this.searchAbort.abort();
    this.searchAbort = new AbortController();
    try {
      const res = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.token
        },
        body: JSON.stringify({
          query: searchQuery,
          variables: { query: query.trim(), first: SEARCH_FIRST }
        }),
        signal: this.searchAbort.signal
      });
      const json = await res.json();
      if (json.errors?.length) return [];
      const nodes = json.data?.search?.nodes ?? [];
      return this.flattenProducts(nodes);
    } catch (err) {
      if (err.name === 'AbortError') return [];
      return [];
    } finally {
      this.searchAbort = null;
    }
  }

  addRow(optionalItem) {
    this.rowIndex += 1;
    const id = this.rowIndex;
    const tpl = this.querySelector('#quick-order-quantity-selector-tpl');
    if (!tpl || !tpl.content) return;

    const REMOVE_ICON_SVG = '<span class="quick-order__remove-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="6.25" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)"/><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width, 1.5)" d="m12.5 7.5-5 5M12.5 12.5l-5-5"/></svg></span>';

    const tr = document.createElement('tr');
    tr.className = 'quick-order__row quick-order__row--empty';
    tr.setAttribute('data-quick-order-row', '');

    const imageCell = document.createElement('td');
    imageCell.className = 'quick-order__cell quick-order__cell--image';
    imageCell.innerHTML = '<span class="quick-order__cell-image quick-order__cell-image--placeholder"></span>';
    tr.appendChild(imageCell);

    const searchCell = document.createElement('td');
    searchCell.className = 'quick-order__cell quick-order__cell--search';
    searchCell.innerHTML = `<div class="quick-order__row-search-wrap">` +
      `<label class="visually-hidden" for="quick-order-row-search-${id}">${escapeHtml(this.t('search_placeholder'))}</label>` +
      `<input type="search" id="quick-order-row-search-${id}" class="quick-order__row-search" placeholder="${escapeHtml(this.t('search_placeholder'))}" autocomplete="off" data-quick-order-row-search aria-label="${escapeHtml(this.t('search_placeholder'))}">` +
      '<div class="quick-order__results quick-order__row-results" role="listbox" data-quick-order-row-results hidden></div>' +
      '</div>';
    tr.appendChild(searchCell);

    const productCell = document.createElement('td');
    productCell.className = 'quick-order__cell';
    productCell.setAttribute('data-quick-order-product', '');
    productCell.textContent = '—';
    tr.appendChild(productCell);

    const qtyCell = document.createElement('td');
    qtyCell.className = 'quick-order__cell quick-order__cell--qty';
    const qtyClone = tpl.content.cloneNode(true);
    const qtyInput = qtyClone.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
    if (qtyInput) qtyInput.id = `quick-order-qty-${id}`;
    qtyCell.appendChild(qtyClone);
    tr.appendChild(qtyCell);

    const subtotalCell = document.createElement('td');
    subtotalCell.className = 'quick-order__cell';
    subtotalCell.setAttribute('data-quick-order-subtotal', '');
    subtotalCell.textContent = '—';
    tr.appendChild(subtotalCell);

    const removeCell = document.createElement('td');
    removeCell.className = 'quick-order__cell quick-order__cell--remove';
    removeCell.innerHTML = `<button type="button" class="quick-order__remove" data-quick-order-remove aria-label="${escapeHtml(this.t('remove'))}" title="${escapeHtml(this.t('remove'))}" style="visibility: hidden">${REMOVE_ICON_SVG}</button>`;
    tr.appendChild(removeCell);

    const quantitiesBeforeAdd = [];
    this.tbody.querySelectorAll('[data-quick-order-row]').forEach((row) => {
      const input = row.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
      quantitiesBeforeAdd.push(input ? input.value : '1');
    });

    this.tbody.appendChild(tr);

    const rowSearch = tr.querySelector('[data-quick-order-row-search]');
    const rowResults = tr.querySelector('[data-quick-order-row-results]');
    const debouncedRun = debounce(() => this.runSearch(rowSearch, rowResults, tr), SEARCH_DEBOUNCE_MS);
    rowSearch.addEventListener('input', debouncedRun);
    rowSearch.addEventListener('focus', () => {
      if ((rowSearch.value || '').trim().length >= SEARCH_MIN_LENGTH) debouncedRun();
    });
    rowSearch.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        rowResults.setAttribute('hidden', '');
        rowResults.innerHTML = '';
        rowSearch.blur();
      }
    });

    const rowQtyInput = tr.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
    if (rowQtyInput) rowQtyInput.addEventListener('input', () => this.updateTotal());

    this.setupTrashAtQty1(tr);

    tr.querySelector('[data-quick-order-remove]').addEventListener('click', () => {
      tr.remove();
      this.updateTotal();
      this.renderMobileCards();
      this.saveState();
    });

    if (optionalItem != null) {
      tr.setAttribute('data-quick-order-skip-mobile-render', '');
      this.selectResult(tr, optionalItem);
    } else if (this.lastSelectedFromMainSearch) {
      tr.setAttribute('data-quick-order-skip-mobile-render', '');
      this.selectResult(tr, this.lastSelectedFromMainSearch);
    }

    const newRowRef = tr;
    const preservedQuantities = quantitiesBeforeAdd.slice();
    const self = this;
    const runRestore = () => {
      const rows = self.tbody?.querySelectorAll('[data-quick-order-row]') || [];
      preservedQuantities.forEach((qty, i) => {
        if (i >= rows.length) return;
        const row = rows[i];
        if (row === newRowRef) return;
        const input = row.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
        if (input) input.value = qty;
      });
      self.updateTrashMinusVisibilityForAllRows();
      self.updateTotal();
      self.renderMobileCards();
    };
    queueMicrotask(runRestore);
  }

  setupTrashAtQty1(rowEl) {
    const qtyWrap = rowEl.querySelector('quantity-selector-component.quick-order__qty-wrap');
    const minusBtn = qtyWrap?.querySelector('button[name="minus"]');
    const qtyInput = rowEl.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
    if (!qtyWrap || !minusBtn || !qtyInput) return;
    const TRASH_ICON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/><path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const trashBtn = document.createElement('button');
    trashBtn.type = 'button';
    trashBtn.className = 'button quantity-minus button-unstyled quick-order__qty-trash';
    trashBtn.setAttribute('name', 'trash');
    trashBtn.setAttribute('aria-label', this.t('remove'));
    trashBtn.innerHTML = `<span class="visually-hidden">${this.t('remove')}</span><span class="svg-wrapper">${TRASH_ICON_SVG}</span>`;
    trashBtn.style.display = qtyInput.value === '1' ? '' : 'none';
    minusBtn.style.display = qtyInput.value === '1' ? 'none' : '';
    minusBtn.parentNode.insertBefore(trashBtn, minusBtn);
    const updateVisibility = () => {
      const v = qtyInput.value;
      const isOne = v === '1' || parseInt(v, 10) === 1;
      trashBtn.style.display = isOne ? '' : 'none';
      minusBtn.style.display = isOne ? 'none' : '';
    };
    qtyInput.addEventListener('input', updateVisibility);
    qtyInput.addEventListener('change', updateVisibility);
    trashBtn.addEventListener('click', (e) => {
      e.preventDefault();
      rowEl.remove();
      this.updateTotal();
      this.renderMobileCards();
    });
  }

  updateTrashMinusVisibility(rowEl) {
    const qtyInput = rowEl?.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
    const minusBtn = rowEl?.querySelector('quantity-selector-component button[name="minus"]');
    const trashBtn = rowEl?.querySelector('.quick-order__qty-trash');
    if (!qtyInput || !minusBtn || !trashBtn) return;
    const v = qtyInput.value;
    const isOne = v === '1' || parseInt(v, 10) === 1;
    trashBtn.style.display = isOne ? '' : 'none';
    minusBtn.style.display = isOne ? 'none' : '';
  }

  updateTrashMinusVisibilityForAllRows() {
    this.tbody?.querySelectorAll('[data-quick-order-row]').forEach((row) => this.updateTrashMinusVisibility(row));
  }

  setupTrashAtQty1ForCard(cardEl, rowEl) {
    const qtyWrap = cardEl.querySelector('quantity-selector-component.quick-order__qty-wrap');
    const minusBtn = qtyWrap?.querySelector('button[name="minus"]');
    const qtyInput = cardEl.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
    if (!qtyWrap || !minusBtn || !qtyInput) return;
    const TRASH_ICON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/><path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const trashBtn = document.createElement('button');
    trashBtn.type = 'button';
    trashBtn.className = 'button quantity-minus button-unstyled quick-order__qty-trash';
    trashBtn.setAttribute('name', 'trash');
    trashBtn.setAttribute('aria-label', this.t('remove'));
    trashBtn.innerHTML = `<span class="visually-hidden">${this.t('remove')}</span><span class="svg-wrapper">${TRASH_ICON_SVG}</span>`;
    trashBtn.style.display = qtyInput.value === '1' ? '' : 'none';
    minusBtn.style.display = qtyInput.value === '1' ? 'none' : '';
    minusBtn.parentNode.insertBefore(trashBtn, minusBtn);
    const updateVisibility = () => {
      const v = qtyInput.value;
      const isOne = v === '1' || parseInt(v, 10) === 1;
      trashBtn.style.display = isOne ? '' : 'none';
      minusBtn.style.display = isOne ? 'none' : '';
    };
    qtyInput.addEventListener('input', updateVisibility);
    qtyInput.addEventListener('change', updateVisibility);
    trashBtn.addEventListener('click', (e) => {
      e.preventDefault();
      rowEl.remove();
      this.updateTotal();
      this.renderMobileCards();
    });
  }

  clearAll() {
    this.tbody.innerHTML = '';
    this.rowIndex = 0;
    this.updateTotal();
    this.renderMobileCards();
    this.hideMessage();
    this.saveState();
  }

  renderMobileCards() {
    if (!this.mobileCards) return;
    const rows = this.tbody?.querySelectorAll('[data-quick-order-row]') || [];
    this.mobileCards.innerHTML = '';
    const tpl = this.querySelector('#quick-order-quantity-selector-tpl');
    const REMOVE_ICON_SVG = '<span class="quick-order__remove-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="6.25" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)"/><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width, 1.5)" d="m12.5 7.5-5 5M12.5 12.5l-5-5"/></svg></span>';

    rows.forEach((tr, index) => {
      const variantId = tr.getAttribute('data-variant-id');
      const handle = tr.getAttribute('data-product-handle') || '';
      const imageUrl = tr.getAttribute('data-image-url') || '';
      const productEl = tr.querySelector('[data-quick-order-product]');
      const product = productEl ? (productEl.querySelector('a') ? productEl.querySelector('a').textContent : productEl.textContent) : '—';
      const rowQtyInput = tr.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
      const qty = rowQtyInput?.value ?? '1';
      const subtotalEl = tr.querySelector('[data-quick-order-subtotal]');
      const subtotal = subtotalEl ? subtotalEl.textContent : '—';
      const skuEl = tr.querySelector('.quick-order__cell-sku');
      const skuText = skuEl ? skuEl.textContent : '—';
      const productUrl = handle ? `/products/${handle}` : '#';

      const card = document.createElement('div');
      card.className = 'quick-order__mobile-card';
      card.setAttribute('data-quick-order-row', '');
      card.setAttribute('data-row-index', String(index));
      if (variantId) card.setAttribute('data-variant-id', variantId);
      card.setAttribute('data-price', tr.getAttribute('data-price') || '0');
      card.setAttribute('data-available', tr.getAttribute('data-available') || 'false');

      let qtyBlock = '';
      if (tpl?.content) {
        const qtyWrap = document.createElement('div');
        qtyWrap.className = 'quick-order__mobile-card-qty';
        const qtyClone = tpl.content.cloneNode(true);
        const cardQtyInput = qtyClone.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
        if (cardQtyInput) {
          cardQtyInput.value = qty;
          cardQtyInput.id = `quick-order-mobile-qty-${index}`;
        }
        qtyWrap.appendChild(qtyClone);
        qtyBlock = qtyWrap.outerHTML;
      }

      const imageHtml = imageUrl
        ? `<a href="${escapeHtml(productUrl)}" class="quick-order__mobile-card-image-link"><img src="${escapeHtml(imageUrl)}" alt="" width="96" height="96" loading="lazy" class="quick-order__mobile-card-image"></a>`
        : '<span class="quick-order__mobile-card-image quick-order__mobile-card-image--placeholder"></span>';
      const productHtml = handle
        ? `<a target="_blank" href="${escapeHtml(productUrl)}" class="quick-order__mobile-card-product-link">${escapeHtml(product)}</a>`
        : escapeHtml(product);
      const skuHtml = handle
        ? `<a target="_blank" href="${escapeHtml(productUrl)}" class="quick-order__mobile-card-sku-link">${escapeHtml(skuText)}</a>`
        : escapeHtml(skuText);

      card.innerHTML =
        `<div class="quick-order__mobile-card-image-wrap">${imageHtml}</div>` +
        '<div class="quick-order__mobile-card-details">' +
        '<div class="quick-order__mobile-card-first-line">' +
        `<span class="quick-order__mobile-card-sku">${skuHtml}</span>` +
        `<button type="button" class="quick-order__remove quick-order__remove--mobile" data-quick-order-remove aria-label="${escapeHtml(this.t('remove'))}">${REMOVE_ICON_SVG}</button>` +
        '</div>' +
        `<span class="quick-order__mobile-card-product">${productHtml}</span>` +
        '<div class="quick-order__mobile-card-row quick-order__mobile-card-row--qty-subtotal">' +
        qtyBlock +
        `<span class="quick-order__mobile-card-subtotal-value" data-quick-order-subtotal>${escapeHtml(subtotal)}</span>` +
        '</div>' +
        '</div>';

      this.mobileCards.appendChild(card);

      const cardSubtotalEl = card.querySelector('[data-quick-order-subtotal]');
      if (cardSubtotalEl) cardSubtotalEl.textContent = subtotal;

      card.querySelector('[data-quick-order-remove]')?.addEventListener('click', () => {
        tr.remove();
        this.updateTotal();
        this.renderMobileCards();
      });

      const cardQtyInput = card.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
      if (cardQtyInput) {
        const rowQtyValue = tr.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]')?.value ?? '1';
        cardQtyInput.value = rowQtyValue;
        const syncRowAndUpdate = () => {
          const rowInput = tr.querySelector('quantity-selector-component input[name="quantity"], quantity-selector-component input[type="number"]');
          if (rowInput) rowInput.value = cardQtyInput.value;
          this.updateTotal();
          const rowSubtotal = tr.querySelector('[data-quick-order-subtotal]');
          if (cardSubtotalEl && rowSubtotal) cardSubtotalEl.textContent = rowSubtotal.textContent;
        };
        cardQtyInput.addEventListener('input', syncRowAndUpdate);
        cardQtyInput.addEventListener('change', () => {
          syncRowAndUpdate();
          this.updateTrashMinusVisibility(tr);
          this.updateMobileCardInPlace(card, tr);
        });
      }
      this.setupTrashAtQty1ForCard(card, tr);
    });
  }

  setupMainSearch() {
    const mainInput = this.querySelector('#quick-order-search-main');
    const mainResults = this.querySelector('#quick-order-results-main');
    const mainSearchReset = this.querySelector('.quick-order__search-reset');
    if (!mainInput || !mainResults) return;
    if (mainSearchReset) {
      mainSearchReset.addEventListener('click', () => {
        mainInput.value = '';
        mainResults.innerHTML = '';
        mainResults.setAttribute('hidden', '');
        mainInput.setAttribute('aria-expanded', 'false');
        this.mainSearchItems = [];
        this.mainSearchFocusedIndex = -1;
        mainInput.focus();
      });
    }
    let firstRow = this.tbody.querySelector('[data-quick-order-row].quick-order__row--empty') || this.tbody.querySelector('[data-quick-order-row]');
    const debouncedRun = debounce(() => {
      firstRow = this.tbody.querySelector('[data-quick-order-row].quick-order__row--empty') || this.tbody.querySelector('[data-quick-order-row]');
      this.runSearch(mainInput, mainResults, firstRow, true);
    }, SEARCH_DEBOUNCE_MS);
    mainInput.addEventListener('input', debouncedRun);
    mainInput.addEventListener('focus', () => {
      if ((mainInput.value || '').trim().length >= SEARCH_MIN_LENGTH) debouncedRun();
      else mainInput.setAttribute('aria-expanded', 'false');
    });
    mainInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        mainResults.setAttribute('hidden', '');
        mainResults.innerHTML = '';
        mainInput.setAttribute('aria-expanded', 'false');
        this.mainSearchItems = [];
        this.mainSearchFocusedIndex = -1;
        mainInput.focus();
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const item = this.mainSearchItems.length && this.mainSearchFocusedIndex >= 0 ? this.mainSearchItems[this.mainSearchFocusedIndex] : null;
        if (item) this.applyMainSearchSelection(item);
        else this.closeMainSearchOverlay();
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const isOpen = mainResults && !mainResults.hasAttribute('hidden') && this.mainSearchItems.length > 0;
        if (e.key === 'ArrowUp' && isOpen && this.mainSearchFocusedIndex <= 0) {
          e.preventDefault();
          this.mainSearchFocusedIndex = -1;
          mainResults.querySelectorAll('.quick-order__result-item[data-index]').forEach((opt) => opt.classList.remove('quick-order__result-item--focused'));
          mainInput.focus();
          return;
        }
        if (isOpen) {
          e.preventDefault();
          let next = this.mainSearchFocusedIndex;
          if (e.key === 'ArrowDown') next = Math.min(this.mainSearchFocusedIndex + 1, this.mainSearchItems.length - 1);
          else next = Math.max(this.mainSearchFocusedIndex - 1, 0);
          this.mainSearchFocusedIndex = next;
          mainResults.querySelectorAll('.quick-order__result-item[data-index]').forEach((opt) => opt.classList.remove('quick-order__result-item--focused'));
          const target = mainResults.querySelector(`.quick-order__result-item[data-index="${next}"]`);
          if (target) {
            target.classList.add('quick-order__result-item--focused');
            target.focus();
          }
        }
      }
    });
  }
}

if (!customElements.get('quick-order-section')) {
  customElements.define('quick-order-section', QuickOrderSection);
}
